import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Download, Eye, Loader2, Play, RotateCcw, SlidersHorizontal, Trash2, Upload } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  cragModelBuildsService,
  type BuildParams,
  type BuildsMeta,
  type CragModelBuild,
  type VideoProbe,
} from '../../../services/crag-model-builds';
import type { Crag } from '../../../types/crag';
import ParamsForm from './ParamsForm';
import { confirmModelReplaced } from './confirm-model-replaced';
import { formatBytes, formatDuration } from './format';

const CragModelViewer = lazy(() => import('../../../components/crag/CragModelViewer'));

/** How often the list is read again while a build is running. */
const POLL_MS = 2000;

const STATUS_VARIANT: Record<CragModelBuild['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  queued: 'outline',
  running: 'secondary',
  done: 'default',
  failed: 'destructive',
  cancelled: 'outline',
};

interface ModelBuilderProps {
  crag: Crag;
  /** The crag after a build was made its model, so the page above updates. */
  onPublished: (crag: Crag) => void;
}

/**
 * Drone video → stylized GLB, on this machine. Shown on a development server
 * only: `list` answers null anywhere else, and the page then shows nothing of
 * it. The heavy lifting is BuildCragModelJob; this is its form and its log.
 */
export default function ModelBuilder({ crag, onPublished }: ModelBuilderProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const slug = crag.slug;

  const [available, setAvailable] = useState<boolean | null>(null);
  const [meta, setMeta] = useState<BuildsMeta | null>(null);
  const [builds, setBuilds] = useState<CragModelBuild[]>([]);
  const [params, setParams] = useState<BuildParams | null>(null);
  const [sourcePath, setSourcePath] = useState('');
  const [probe, setProbe] = useState<VideoProbe | null>(null);
  const [probing, setProbing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const fail = useCallback(
    (error: unknown) =>
      toast({
        title: t('cragModel.builder.error'),
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      }),
    [t, toast],
  );

  const load = useCallback(async () => {
    const result = await cragModelBuildsService.list(slug);
    if (!result) {
      setAvailable(false);
      return;
    }
    setAvailable(true);
    setBuilds(result.builds);
    setMeta(result.meta);
    setParams((current) => current ?? result.meta.defaults);
    setSourcePath((current) => current || result.meta.last_source_path || '');
  }, [slug]);

  useEffect(() => {
    load().catch(fail);
  }, [load, fail]);

  const running = builds.some((build) => build.status === 'queued' || build.status === 'running');
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => load().catch(() => undefined), POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, load]);

  if (!available || !meta || !params) return null;

  const missingTools = (Object.entries(meta.tools) as [keyof BuildsMeta['tools'], boolean][])
    .filter(([, present]) => !present)
    .map(([tool]) => t(`cragModel.tools.${tool}`));

  const readVideo = async () => {
    setProbing(true);
    try {
      setProbe(await cragModelBuildsService.probe(slug, sourcePath));
    } catch (error) {
      setProbe(null);
      fail(error);
    } finally {
      setProbing(false);
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      const build = await cragModelBuildsService.create(slug, sourcePath, params);
      setBuilds((current) => [build, ...current]);
    } catch (error) {
      fail(error);
    } finally {
      setStarting(false);
    }
  };

  const act = async (action: () => Promise<unknown>) => {
    try {
      await action();
      await load();
    } catch (error) {
      fail(error);
    }
  };

  const preview = builds.find((build) => build.id === previewId && build.glb_url);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {t('cragModel.builder.title')}
          <Badge variant="outline">{t('cragModel.builder.localOnly')}</Badge>
        </CardTitle>
        <CardDescription>{t('cragModel.builder.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {missingTools.length > 0 && (
          <Alert variant="destructive">
            <AlertTitle>{t('cragModel.builder.missingTools')}</AlertTitle>
            <AlertDescription>{missingTools.join(', ')}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="crag-model-source">{t('cragModel.builder.source')}</Label>
          <div className="flex flex-wrap gap-2">
            <Input
              id="crag-model-source"
              className="min-w-0 flex-1 font-mono text-sm"
              placeholder="/Users/…/DJI_0062.MP4"
              value={sourcePath}
              onChange={(e) => {
                setSourcePath(e.target.value);
                setProbe(null);
              }}
            />
            <Button type="button" variant="outline" disabled={!sourcePath || probing} onClick={readVideo}>
              {probing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('cragModel.builder.readVideo')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('cragModel.builder.sourceHint')}</p>
          {probe && (
            <p className="text-sm">
              {t('cragModel.builder.probe', {
                duration: formatDuration(probe.duration),
                width: probe.width,
                height: probe.height,
                fps: probe.fps ?? '?',
                size: formatBytes(probe.bytes),
                frames: Math.round(
                  ((Number(params.frames.end_seconds) || probe.duration) - Number(params.frames.start_seconds)) *
                    Number(params.frames.sample_fps),
                ),
              })}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label>{t('cragModel.builder.params')}</Label>
            <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={() => setParams(meta.defaults)}>
              <RotateCcw className="h-4 w-4" />
              {t('cragModel.builder.resetParams')}
            </Button>
          </div>
          <ParamsForm schema={meta.param_schema} value={params} onChange={setParams} />
          <p className="text-xs text-muted-foreground">{t('cragModel.builder.cacheHint')}</p>
        </div>

        <Button type="button" className="gap-2" disabled={!sourcePath || starting} onClick={start}>
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {t('cragModel.builder.start')}
        </Button>

        {builds.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('cragModel.builder.builds')}</h3>
            <ul className="divide-y rounded-lg border">
              {builds.map((build) => (
                <li key={build.id} className="space-y-2 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={STATUS_VARIANT[build.status]}>{t(`cragModel.status.${build.status}`)}</Badge>
                    {build.published && <Badge variant="secondary">{t('cragModel.builder.published')}</Badge>}
                    <span className="font-mono text-xs">{build.source_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(build.created_at).toLocaleString(i18n.language)}
                    </span>
                  </div>

                  {(build.status === 'running' || build.status === 'queued') && (
                    <div className="space-y-1">
                      <div className="h-2 overflow-hidden rounded bg-muted">
                        <div
                          className="h-full bg-primary transition-[width]"
                          style={{ width: `${Math.round(build.progress * 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {build.step ? t(`cragModel.steps.${build.step}`) : t('cragModel.status.queued')} ·{' '}
                        {Math.round(build.progress * 100)}%
                      </p>
                    </div>
                  )}

                  {build.error_message && (
                    <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded bg-destructive/10 p-2 text-xs text-destructive">
                      {build.error_message}
                    </pre>
                  )}

                  <BuildStats build={build} />

                  <div className="flex flex-wrap gap-2">
                    {build.glb_url && (
                      <Button size="sm" variant="outline" className="gap-2" onClick={() => setPreviewId(build.id)}>
                        <Eye className="h-4 w-4" />
                        {t('cragModel.builder.preview')}
                      </Button>
                    )}
                    {build.glb_download_url && (
                      <Button size="sm" variant="outline" className="gap-2" asChild>
                        <a href={build.glb_download_url} download>
                          <Download className="h-4 w-4" />
                          {t('cragModel.builder.download')}
                        </a>
                      </Button>
                    )}
                    {build.glb_url && !build.published && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={() => {
                          if (!confirmModelReplaced(crag, t)) return;
                          act(async () => onPublished(await cragModelBuildsService.publish(slug, build.id)));
                        }}
                      >
                        <Upload className="h-4 w-4" />
                        {t('cragModel.builder.publish')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-2"
                      onClick={() => {
                        setParams(build.params);
                        setSourcePath(build.source_path);
                      }}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      {t('cragModel.builder.useParams')}
                    </Button>
                    {build.status === 'running' || build.status === 'queued' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-2 text-destructive"
                        onClick={() => act(() => cragModelBuildsService.cancel(slug, build.id))}
                      >
                        <Ban className="h-4 w-4" />
                        {t('cragModel.builder.cancel')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-2 text-destructive"
                        onClick={() => act(() => cragModelBuildsService.destroy(slug, build.id))}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('cragModel.builder.delete')}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {preview?.glb_url && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t('cragModel.builder.previewOf', { name: preview.source_name })}</h3>
            <Suspense fallback={<div className="h-[28rem] rounded-lg border bg-muted/40" />}>
              <CragModelViewer url={preview.glb_url} />
            </Suspense>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BuildStats({ build }: { build: CragModelBuild }) {
  const { t } = useTranslation();
  const { frames, reconstruction, style } = build.stats;
  const parts = [
    frames && t('cragModel.stats.frames', { count: frames.count }),
    reconstruction && t('cragModel.stats.reconstruction', { faces: reconstruction.faces.toLocaleString() }),
    style &&
      t('cragModel.stats.style', { faces: style.faces.toLocaleString(), size: formatBytes(style.bytes) }),
  ].filter(Boolean);
  if (parts.length === 0) return null;
  const reused = [frames?.reused && t('cragModel.steps.frames'), reconstruction?.reused && t('cragModel.steps.reconstruction')]
    .filter(Boolean)
    .join(', ');

  return (
    <p className="text-xs text-muted-foreground">
      {parts.join(' · ')}
      {reused && ` · ${t('cragModel.stats.reused', { steps: reused })}`}
    </p>
  );
}
